// Duenner Client fuer das E-Invoicing-Backend unter /api/einvoicing.
// MONOLITH-KONVENTION (wie plant-maintenance/api.js): `user` ist das ERSTE Argument
// jeder Funktion und traegt den JWT (.token, mock-Token wird uebersprungen).
// Schreiboperationen Richtung SAP tragen einen Idempotency-Key — ein Retry darf
// in SAP niemals ein Duplikat erzeugen.

const einvCall = async (endpoint, method = 'GET', data = null, user = null, extraHeaders = {}) => {
  const config = { method, headers: { ...extraHeaders } };
  if (user?.token && !user.token.startsWith('mock-')) {
    config.headers['Authorization'] = `Bearer ${user.token}`;
  }
  if (data instanceof FormData) {
    config.body = data; // Browser setzt multipart-Boundary selbst
  } else if (data) {
    config.headers['Content-Type'] = 'application/json';
    config.body = JSON.stringify(data);
  }
  const response = await fetch(`/api/einvoicing${endpoint}`, config);
  if (!response.ok) {
    let body = null;
    try { body = await response.json(); } catch { /* kein JSON-Body */ }
    if (body && body.ok === false) return body; // Business-Failure: Screens zeigen body.error
    const err = new Error(body?.error || body?.message || `HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return response.json();
};

const idemKey = () => `einv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// ==================== E-INVOICING-MODUL (/api/einvoicing/*) ====================

export const getDashboard = (user, plant) =>
  einvCall(`/dashboard${plant ? `?plant=${encodeURIComponent(plant)}` : ''}`, 'GET', null, user);
export const getRegimes = (user) => einvCall('/regimes', 'GET', null, user);
export const getCapabilities = (user) => einvCall('/capabilities', 'GET', null, user);
export const getFormats = (user) => einvCall('/formats', 'GET', null, user);
/** Fuer den User sichtbare Werke: { all, plants: [{plant,name}] } — Grundlage des "Alle Werke"-Filters. */
export const getPlants = (user) => einvCall('/plants', 'GET', null, user);
/** Werk einer Rechnung manuell setzen ('' = entfernen). */
export const setPlant = (user, id, plant) => einvCall(`/invoices/${id}/plant`, 'POST', { plant }, user);

export const listInvoices = (user, params = {}) => {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== false)
    .map(([k, v]) => [k, v === true ? '1' : String(v)]));
  return einvCall(`/invoices?${q.toString()}`, 'GET', null, user);
};

export const getInvoice = (user, id) => einvCall(`/invoices/${id}`, 'GET', null, user);

export const uploadInvoice = (user, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return einvCall('/invoices/upload', 'POST', fd, user);
};

export const revalidate = (user, id) => einvCall(`/invoices/${id}/revalidate`, 'POST', {}, user);
export const rematch = (user, id) => einvCall(`/invoices/${id}/match`, 'POST', {}, user);
export const approveInvoice = (user, id, comment) => einvCall(`/invoices/${id}/approve`, 'POST', { comment }, user);
export const rejectInvoice = (user, id, comment) => einvCall(`/invoices/${id}/reject`, 'POST', { comment }, user);
export const postToSap = (user, id) => einvCall(`/invoices/${id}/post-to-sap`, 'POST', {}, user, { 'Idempotency-Key': idemKey() });

export const listSapBillingDocs = (user, q) => einvCall(`/sap/billing-docs${q ? `?q=${encodeURIComponent(q)}` : ''}`, 'GET', null, user);
export const createOutbound = (user, payload) => einvCall('/outbound', 'POST', payload, user);
export const generateInvoice = (user, id, format, cius) => einvCall(`/invoices/${id}/generate`, 'POST', { format, cius }, user);
export const markSent = (user, id, channel) => einvCall(`/invoices/${id}/mark-sent`, 'POST', { channel }, user);

// Ausgangsrechnung von Hand: Verkaeuferdaten + Nummernkreis (PUT nur Admin), Erfassung.
export const getSeller = (user) => einvCall('/seller', 'GET', null, user);
export const saveSeller = (user, seller) => einvCall('/seller', 'PUT', seller, user);
export const getNumberRange = (user) => einvCall('/number-range', 'GET', null, user);
export const saveNumberRange = (user, range) => einvCall('/number-range', 'PUT', range, user);
export const createManualOutbound = (user, payload) => einvCall('/outbound/manual', 'POST', payload, user);

/** Download-URL einer archivierten Datei (Original/generiert) — laeuft ueber fetch wegen JWT. */
export const downloadFile = async (user, invoiceId, fileId, filename) => {
  const config = { headers: {} };
  if (user?.token && !user.token.startsWith('mock-')) config.headers['Authorization'] = `Bearer ${user.token}`;
  const res = await fetch(`/api/einvoicing/invoices/${invoiceId}/files/${fileId}`, config);
  if (!res.ok) throw new Error(`Download fehlgeschlagen (HTTP ${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'datei';
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

/**
 * Blob-URL einer Datei fuer die Inline-Vorschau (PDF im iframe) — Aufrufer
 * muss die URL nach Gebrauch mit URL.revokeObjectURL freigeben.
 */
export const fileBlobUrl = async (user, invoiceId, fileId) => {
  const config = { headers: {} };
  if (user?.token && !user.token.startsWith('mock-')) config.headers['Authorization'] = `Bearer ${user.token}`;
  const res = await fetch(`/api/einvoicing/invoices/${invoiceId}/files/${fileId}?inline=1`, config);
  if (!res.ok) throw new Error(`Datei laden fehlgeschlagen (HTTP ${res.status})`);
  return URL.createObjectURL(await res.blob());
};

const api = {
  getDashboard, getRegimes, getFormats, getCapabilities, getPlants, setPlant, listInvoices, getInvoice, uploadInvoice,
  revalidate, rematch, approveInvoice, rejectInvoice, postToSap,
  listSapBillingDocs, createOutbound, generateInvoice, markSent, downloadFile, fileBlobUrl,
  getSeller, saveSeller, getNumberRange, saveNumberRange, createManualOutbound,
};
export default api;
